import { memo, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import ReactECharts from 'echarts-for-react'
import { format as chartFormat, type ECharts, type EChartsOption } from 'echarts'
import type { Time } from 'lightweight-charts'
import type { ScreenerCVDCandle } from '@/types'
import { SCREENER_INTERVAL_SECONDS } from '@/lib/screenerHistory'
import { ChartCollapseButton } from './ChartCollapseButton'
import { useChartTimeNavigation } from './ChartTimeNavigation'
import { useChartCrosshair, chartTooltipTime } from './useChartCrosshair'
import { useChartResize } from './useChartResize'
import { useVisibleChartScale } from './useVisibleChartScale'
import { formatCompact, formatUtc, formatUtcAxis } from './chartUtils'
import { buildCVDPlot, cvdCandleAtTime } from './cvdChartData'

const UP_COLOR = '#26a69a'
const DOWN_COLOR = '#f23645'

interface CVDChartProps {
  candles: ScreenerCVDCandle[]
  symbol: string
  exchange: string
  interval: string
  chartGroup: string
  onCrosshairMove: (time: Time | null) => void
  syncedTime: Time | null
  height?: number
  showXAxis?: boolean
  collapsed?: boolean
  onToggleCollapse?: () => void
  orderControls?: ReactNode
  unit?: 'coins' | 'USD'
  baselineTimestamp?: number | null
  coverageNote?: string
  hasGaps?: boolean
  loading?: boolean
  error?: string | null
}

function CVDChartComponent({
  candles, symbol, exchange, interval, chartGroup, onCrosshairMove, syncedTime,
  height = 200, showXAxis = true, collapsed = false, onToggleCollapse, orderControls,
  unit = 'coins', baselineTimestamp, coverageNote, hasGaps = false, loading = false, error,
}: CVDChartProps) {
  const chartBodyId = useId()
  const chartRef = useRef<ReactECharts>(null)
  const [chart, setChart] = useState<ECharts | null>(null)
  useChartResize(chart)
  const timeNavigation = useChartTimeNavigation(chart)
  const derived = useMemo(() => buildCVDPlot(candles), [candles])
  const intervalSeconds = SCREENER_INTERVAL_SECONDS[interval] || 60
  const { fitVisibleScale, onChartReady } = useVisibleChartScale(
    derived.scaleSeries, `${symbol}:${exchange}:cvd:${interval}`, true,
  )
  const selected = useMemo(() => syncedTime == null ? derived.latest
    : cvdCandleAtTime(derived, Number(syncedTime) * 1000, intervalSeconds), [derived, syncedTime, intervalSeconds])
  const selectedColor = selected && selected.close < selected.open ? DOWN_COLOR : UP_COLOR
  const baseline = baselineTimestamp ?? derived.points[0]?.timestamp
  const coverageDescription = coverageNote || `Cumulative buy minus sell volume in ${unit}. Empty intervals carry the last recorded total and are marked stale; missing history may hide unrecorded trades. CVD starts at zero at the loaded history start; the baseline changes when the loaded history changes.${baseline != null ? ` Zero baseline: ${formatUtc(baseline * 1000)}.` : ''}`
  const emptyMessage = error || (loading ? 'Loading cumulative volume delta…' : 'No stored trade data for this time range')

  const option = useMemo<EChartsOption>(() => {
    const latestColor = derived.latest && derived.latest.close < derived.latest.open ? DOWN_COLOR : UP_COLOR
    return {
      animation: false,
      useUTC: true,
      backgroundColor: 'transparent',
      grid: { left: 10, right: 76, top: 6, bottom: showXAxis ? 28 : 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross', lineStyle: { color: '#64748b', type: 'dashed' } },
        backgroundColor: 'rgba(5, 10, 18, 0.96)',
        borderColor: '#334155',
        textStyle: { color: '#e2e8f0', fontSize: 11 },
        formatter: (raw: any) => {
          const entries = Array.isArray(raw) ? raw : [raw]
          const axisMs = chartTooltipTime(chartRef.current?.getEchartsInstance(), Number(entries[0]?.axisValue ?? 0))
          const point = cvdCandleAtTime(derived, axisMs, intervalSeconds)
          const title = `<div style="color:#94a3b8;margin-bottom:5px">${formatUtc(axisMs, interval === '5s')}</div>`
          if (!point) return `${title}<div>CVD: N/A · no stored trade bucket</div>`
          const color = point.close < point.open ? DOWN_COLOR : UP_COLOR
          return `${title}
            <div>${chartFormat.encodeHTML(exchange.toUpperCase())} CVD · ${unit}</div>
            <div style="color:${color}"><b>${formatCompact(point.close, 3)}</b> ${unit}</div>
            ${point.carriedForward
              ? '<div style="color:#f59e0b;font-size:10px">Stale · filled with last recorded CVD; no stored trade bucket</div>'
              : `<div>Delta: <b>${formatCompact(point.delta ?? point.close - point.open, 3)}</b> ${unit}</div>${point.complete === false ? '<div style="color:#f59e0b;font-size:10px">Partial bucket</div>' : ''}`}`
        },
      },
      xAxis: {
        type: 'time',
        axisPointer: { snap: false },
        ...timeNavigation.axis,
        axisLine: { show: showXAxis, lineStyle: { color: '#253044' } },
        axisTick: { show: false },
        axisLabel: { show: showXAxis, color: '#64748b', fontSize: 9, hideOverlap: true,
          formatter: (value: number) => formatUtcAxis(value, interval) },
        splitLine: { show: true, lineStyle: { color: '#172033' } },
      },
      yAxis: {
        type: 'value', position: 'right', triggerEvent: true, scale: true,
        axisLine: { show: false }, axisTick: { show: false },
        axisLabel: { color: '#94a3b8', fontSize: 10, formatter: (value: number) => formatCompact(value) },
        axisPointer: { label: { formatter: (params: { value: unknown }) => formatCompact(Number(params.value), 3) } },
        splitLine: { lineStyle: { color: '#172033' } },
      },
      dataZoom: [{
        id: 'screener-time', ...timeNavigation.zoom, type: 'inside', xAxisIndex: [0],
        zoomOnMouseWheel: true, moveOnMouseWheel: false, moveOnMouseMove: true, filterMode: 'none',
      }, {
        id: 'screener-cvd-scale', type: 'inside', yAxisIndex: [0],
        zoomOnMouseWheel: 'shift', moveOnMouseMove: 'alt', moveOnMouseWheel: false, filterMode: 'none',
      }],
      series: [{
        id: 'cvd-line', name: 'CVD', type: 'line',
        step: 'end', showSymbol: derived.points.length === 1, connectNulls: false,
        lineStyle: { color: UP_COLOR, width: 1.5 },
        itemStyle: { color: UP_COLOR },
        emphasis: { disabled: true },
        data: derived.series,
        markLine: {
          silent: true, symbol: 'none', z: 10,
          lineStyle: { color: latestColor, opacity: 0.35, width: 1, type: 'dotted' },
          label: { show: true, position: 'end', color: '#ffffff', backgroundColor: latestColor, opacity: 1,
            padding: [3, 5], fontSize: 10, formatter: () => formatCompact(derived.latest!.close, 3) },
          data: derived.latest ? [{ yAxis: derived.latest.close }] : [],
        },
      }],
    }
  }, [derived, exchange, interval, intervalSeconds, showXAxis, timeNavigation, unit])

  useChartCrosshair(chart, chartGroup, onCrosshairMove, interval)

  return (
    <div data-chart-collapsed={collapsed} className="relative overflow-hidden rounded-lg border border-slate-800/80 bg-[#080d16]">
      <div className="relative z-10 flex min-h-[34px] flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {orderControls}
          <ChartCollapseButton collapsed={collapsed} onToggle={onToggleCollapse} label={`${exchange} CVD chart`} controlsId={chartBodyId} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-300">{exchange}</span>
          <span className="text-[11px] text-slate-400">Cumulative Volume Delta (CVD)</span>
          <span className="text-[9px] uppercase text-slate-500">Futures · {unit}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span title={coverageDescription} className="cursor-help text-[9px] text-slate-500">Trade aggregates · From chart start</span>
          {hasGaps && <span title="Some trade history is unavailable or incomplete; CVD uses the recorded trades only." className="cursor-help text-[9px] text-amber-400">Partial history</span>}
          {selected?.carriedForward
            ? <span className="text-[9px] text-amber-400">Stale</span>
            : selected?.complete === false && <span className="text-[9px] text-amber-400">Partial bucket</span>}
          <button type="button" onClick={fitVisibleScale} disabled={!selected}
            title="Click to fit CVD scale to the visible time range" aria-label={`Fit ${exchange} CVD scale to visible time range`}
            className="flex flex-wrap items-center gap-x-2 rounded px-1 py-0.5 font-mono text-[11px] hover:bg-slate-800/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-300 disabled:cursor-default"
            style={{ color: selected ? selectedColor : '#64748b' }}>
            {selected ? formatCompact(selected.close, 3) : '—'}
          </button>
        </div>
      </div>
      <div id={chartBodyId} className="relative" style={{ display: collapsed ? 'none' : undefined }} aria-hidden={collapsed || undefined}>
        <ReactECharts ref={chartRef} option={option} style={{ height: Math.max(80, height - 34), width: '100%' }} autoResize={false}
          onChartReady={(instance) => { instance.group = chartGroup; onChartReady(instance); setChart(instance) }}
          replaceMerge={['series']} lazyUpdate />
        {derived.points.length === 0 && <div role="status" className="pointer-events-none absolute inset-0 flex items-center justify-center px-4 text-center text-xs text-slate-500">{emptyMessage}</div>}
        {derived.points.length > 0 && error && <div role="status" className="absolute left-3 top-0 text-[9px] text-amber-400">{error}</div>}
      </div>
    </div>
  )
}

export const CVDChart = memo(CVDChartComponent)
